import { JSONContent } from '@tiptap/react';
import { EditorInstance } from 'novel';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { generateJSON } from '@tiptap/html';
import { Extension } from '@tiptap/core';
import { configureMarkedWithImageSize } from '../extensions/image-size-extension';
// @ts-ignore
import { gfm } from 'turndown-plugin-gfm';

/**
 * Utility functions for converting between different content formats
 * (markdown, HTML, JSON) for the editor
 */

/**
 * Count words in a text string
 */
export function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Parse initial content into the format expected by the editor
 */
export function parseInitialContent(
  initialContent: string | JSONContent,
  format: 'markdown' | 'html' | 'json',
  extensions: Extension[]
): JSONContent {
  // If content is already in JSON format, return it
  if (typeof initialContent !== 'string') {
    return initialContent as JSONContent;
  }

  if (!initialContent || initialContent.trim() === '') {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }]
    };
  }

  // If format is markdown, convert markdown to HTML first, then to JSON
  if (format === 'markdown') {
    try {
      // Configure marked with image size extension
      configureMarkedWithImageSize();

      // Convert markdown to HTML
      const html = marked.parse(initialContent) as string;
      // Convert HTML to JSON
      return generateJSON(html, extensions);
    } catch (error) {
      console.error('Error converting markdown to JSON:', error);
    }
  }

  // Fallback or if format is not markdown
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [{ type: 'text', text: initialContent }]
      }
    ]
  };
}

/**
 * Get formatted content from the editor based on the desired format
 */
export function getFormattedContent(editor: EditorInstance, format: 'markdown' | 'html' | 'json' = 'markdown'): string | JSONContent {
  if (format === 'json') {
    return editor.getJSON();
  }

  if (format === 'html') {
    return editor.getHTML();
  }

  // Default: convert to markdown
  const html = editor.getHTML();
  return htmlToMarkdown(html);
}

/**
 * Convert HTML string to Markdown using TurndownService
 */
export function htmlToMarkdown(html: string): string {
  // console.log('📝 CONTENT FORMATTER: Converting HTML to markdown');

  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced'
  });

  // Enable GFM (GitHub Flavored Markdown) support, including tables
  turndownService.use(gfm);

  // Add custom rule for images with size attributes
  turndownService.addRule('imageWithSize', {
    filter: 'img',
    replacement: function (content, node) {
      const element = node as HTMLElement;

      const alt = element.getAttribute('alt') || '';
      const src = element.getAttribute('src') || '';
      const style = element.getAttribute('style') || '';

      let sizeInfo = '';

      const widthMatch = style.match(/width:\s*([0-9]+(?:\.[0-9]+)?)px/);
      const heightMatch = style.match(/height:\s*([0-9]+(?:\.[0-9]+)?)px/);

      const width = widthMatch ? parseFloat(widthMatch[1]).toFixed(0) : '';
      const height = heightMatch ? parseFloat(heightMatch[1]).toFixed(0) : '';

      if (width || height) {
        sizeInfo = ` =${width}x${height}`;
      }

      const result = `![${alt}](${src}${sizeInfo})`;
      return result;
    },
  });

  const cleanedHtml = cleanHtmlForTurndown(html);
  return turndownService.turndown(cleanedHtml);
}

/**
 * Clean HTML to make it more friendly for Turndown conversion,
 * especially for tables.
 */
function cleanHtmlForTurndown(html: string): string {
  if (typeof window === 'undefined') return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const tables = doc.querySelectorAll('table');
    tables.forEach(table => {
      // Remove colgroups as they are not supported in markdown
      const colgroups = table.querySelectorAll('colgroup');
      colgroups.forEach(cg => cg.remove());

      // Remove class and style attributes from table
      table.removeAttribute('class');
      table.removeAttribute('style');

      // Unwrap paragraphs in cells (markdown tables don't support block content)
      const cells = table.querySelectorAll('td, th');
      cells.forEach(cell => {
        // Remove colspan/rowspan if they are 1
        if (cell.getAttribute('colspan') === '1') cell.removeAttribute('colspan');
        if (cell.getAttribute('rowspan') === '1') cell.removeAttribute('rowspan');

        const paragraphs = cell.querySelectorAll('p');
        paragraphs.forEach(p => {
          // Replace p with its content
          const parent = p.parentNode;
          if (parent) {
            while (p.firstChild) {
              parent.insertBefore(p.firstChild, p);
            }
            // Add a space if it's not the last element, to separate content
            if (p.nextSibling) {
              const space = doc.createTextNode(' ');
              parent.insertBefore(space, p);
            }
            parent.removeChild(p);
          }
        });
      });

      // Ensure thead exists if there are th elements in the first row of tbody
      const thead = table.querySelector('thead');
      const tbody = table.querySelector('tbody');
      if (!thead && tbody) {
        const firstRow = tbody.querySelector('tr');
        if (firstRow && firstRow.querySelector('th')) {
          const newThead = doc.createElement('thead');
          newThead.appendChild(firstRow);
          table.insertBefore(newThead, tbody);
        }
      }
    });

    return doc.body.innerHTML;
  } catch (e) {
    console.error('Error cleaning HTML for turndown:', e);
    return html;
  }
}
